<template>
  <div class="base-map-container">
    <l-map 
      ref="leafletMap"
      style="height: 100%;" 
      :zoom="zoom" 
      :center="center" 
      :options="mapOptions" 
      @update:center="centerUpdated" 
      @update:zoom="zoomUpdated" 
      @ready="onMapReady"
      @mousemove="onMouseMove"
      @click="onMapClick">
      <l-tile-layer 
        v-for="layer in layers"
        :key="layer.name"
        :url="layer.url"
        :attribution="layer.attribution"
        :subdomains="layer.subdomains"
        :layer-type="layer.layerType"
        :visible="layer.visible"
        :options="layer.options"
      />
      
      <!-- 添加比例尺控件 -->
      <l-control-scale position="bottomleft" :imperial="false" :metric="true"></l-control-scale>
      
      <!-- 插槽用于父组件添加额外的地图元素 -->
      <slot name="map-overlays"></slot>
    </l-map>
    
    <!-- 经纬度显示面板 -->
    <div class="map-controls-wrapper" v-if="showCoordinates">
      <div class="coordinate-panel">
        <div class="coordinate-line">经度 {{ formatLongitude }}</div>
        <div class="coordinate-line">纬度 {{ formatLatitude }}</div>
      </div>
    </div>
    
    <!-- 缩放控制按钮 -->
    <div class="map-footer">
      <div class="zoom-controls">
        <div class="zoom-control-btn" @click="zoomIn">
          <i class="el-icon-plus"></i>
        </div>
        <div class="zoom-control-btn" @click="zoomOut">
          <i class="el-icon-minus"></i>
        </div>
        <!-- 定位工具 - 仅在桥梁管理页面显示 -->
        <div v-if="isBridgeManagementPage" class="zoom-control-btn" :class="{'active-tool': showLocationDialog}" @click="toggleLocationDialog">
          <i class="el-icon-position"></i>
        </div>
        <!-- 测距工具 - 在桥梁管理页面或启用showMeasureTool时显示 -->
        <div v-if="isBridgeManagementPage || showMeasureTool" class="zoom-control-btn" :class="{'active-tool': activeTool === 'measure'}" @click="toggleTool('measure')">
          <i class="el-icon-s-unfold"></i>
        </div>
      </div>
    </div>
    
    <!-- 全屏控制按钮 -->
    <div class="map-control-buttons">
      <div class="map-control-icon" v-if="showFullscreen" @click="toggleFullscreen">
        <i :class="isFullscreen ? 'el-icon-close' : 'el-icon-full-screen'"></i>
      </div>
      <!-- 插槽用于父组件添加额外的控制按钮 -->
      <slot name="control-buttons"></slot>
    </div>
    
    <!-- 🎯 定位工具对话框 -->
    <el-dialog
      title="坐标定位1"
      :visible.sync="showLocationDialog"
      width="450px"
      :append-to-body="true"
      :close-on-click-modal="false">
      <div class="location-input-container">
        <!-- 当前地图中心点坐标显示 -->
        <div class="current-map-center">
          <h4>桥梁位置</h4>
          <div class="center-coords">
            <template v-if="coordinateFormat === 'decimal'">
              <p><strong>纬度:</strong> {{ currentMapCenter.decimal.lat }}° {{ currentMapCenter.decimal.lat >= 0 ? 'N' : 'S' }}</p>
              <p><strong>经度:</strong> {{ currentMapCenter.decimal.lng }}° {{ currentMapCenter.decimal.lng >= 0 ? 'E' : 'W' }}</p>
            </template>
            <template v-else>
              <p><strong>纬度:</strong> {{ currentMapCenter.dms.lat.degrees }}° {{ currentMapCenter.dms.lat.minutes }}' {{ currentMapCenter.dms.lat.seconds }}" {{ currentMapCenter.dms.lat.isNorth ? 'N' : 'S' }}</p>
              <p><strong>经度:</strong> {{ currentMapCenter.dms.lng.degrees }}° {{ currentMapCenter.dms.lng.minutes }}' {{ currentMapCenter.dms.lng.seconds }}" {{ currentMapCenter.dms.lng.isEast ? 'E' : 'W' }}</p>
            </template>
          </div>
        </div>
        
        <el-divider></el-divider>
        
        <el-form label-width="80px">
          <el-form-item label="输入格式:">
            <el-radio-group v-model="coordinateFormat" size="small">
              <el-radio label="decimal">十进制度数</el-radio>
              <el-radio label="dms">度分秒</el-radio>
            </el-radio-group>
          </el-form-item>
          
          <!-- 十进制度数输入 - 使用switch控件，普通文本输入 -->
          <template v-if="coordinateFormat === 'decimal'">
            <el-form-item label="纬度:">
              <div class="decimal-switch-row">
                <el-input
                  v-model="decimalCoords.lat"
                  placeholder="如: 31.400"
                  class="decimal-switch-input"
                  @keyup.enter.native="gotoLocation">
                </el-input>
                <span class="decimal-symbol">°</span>
                <el-switch
                  v-model="decimalCoords.isNorth"
                  active-text="N"
                  inactive-text="S"
                  active-color="#409EFF"
                  inactive-color="#F56C6C"
                  size="small">
                </el-switch>
              </div>
            </el-form-item>
            <el-form-item label="经度:">
              <div class="decimal-switch-row">
                <el-input
                  v-model="decimalCoords.lng"
                  placeholder="如: 121.539"
                  class="decimal-switch-input"
                  @keyup.enter.native="gotoLocation">
                </el-input>
                <span class="decimal-symbol">°</span>
                <el-switch
                  v-model="decimalCoords.isEast"
                  active-text="E"
                  inactive-text="W"
                  active-color="#409EFF"
                  inactive-color="#F56C6C"
                  size="small">
                </el-switch>
              </div>
            </el-form-item>
          </template>
          
          <!-- 度分秒输入 - 精简模式 -->
          <template v-else>
            <el-form-item label="纬度:">
              <div class="dms-compact-row">
                <el-input v-model="dmsCoords.lat.degrees" placeholder="0" size="small" class="dms-compact-input"></el-input>
                <span class="dms-compact-symbol">°</span>
                <el-input v-model="dmsCoords.lat.minutes" placeholder="0" size="small" class="dms-compact-input"></el-input>
                <span class="dms-compact-symbol">'</span>
                <el-input v-model="dmsCoords.lat.seconds" placeholder="0.0" size="small" class="dms-compact-input"></el-input>
                <span class="dms-compact-symbol">"</span>
                <el-switch
                  v-model="dmsCoords.lat.isNorth"
                  active-text="N"
                  inactive-text="S"
                  active-color="#409EFF"
                  inactive-color="#F56C6C"
                  size="small">
                </el-switch>
              </div>
            </el-form-item>
            <el-form-item label="经度:">
              <div class="dms-compact-row">
                <el-input v-model="dmsCoords.lng.degrees" placeholder="0" size="small" class="dms-compact-input"></el-input>
                <span class="dms-compact-symbol">°</span>
                <el-input v-model="dmsCoords.lng.minutes" placeholder="0" size="small" class="dms-compact-input"></el-input>
                <span class="dms-compact-symbol">'</span>
                <el-input v-model="dmsCoords.lng.seconds" placeholder="0.0" size="small" class="dms-compact-input"></el-input>
                <span class="dms-compact-symbol">"</span>
                <el-switch
                  v-model="dmsCoords.lng.isEast"
                  active-text="E"
                  inactive-text="W"
                  active-color="#409EFF"
                  inactive-color="#F56C6C"
                  size="small">
                </el-switch>
              </div>
            </el-form-item>
          </template>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showLocationDialog = false">取消</el-button>
        <el-button type="primary" @click="gotoLocation">定位</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import BaseMapJs from './BaseMap.js'
export default BaseMapJs
</script>

<style scoped>
.base-map-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
}

.map-footer {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.map-controls-wrapper {
  position: absolute;
  bottom: 25px;
  left: 15px;
  z-index: 1000;
}

.coordinate-panel {
  background-color: #fff;
  border-radius: 4px;
  padding: 6px 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  min-width: 120px;
  margin-bottom: 5px;
  border: 1px solid #e0e0e0;
}

.coordinate-line {
  color: #222;
  font-size: 12px;
  font-family: 'Consolas', monospace;
  text-align: left;
  margin: 2px 0;
  white-space: nowrap;
}

/* 控制按钮样式 */
.map-control-buttons {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.map-control-icon {
  width: 32px;
  height: 32px;
  background-color: rgba(28, 47, 123, 0.85);
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #e3eafc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s;
}

.map-control-icon:hover {
  background-color: #3a5eea;
  color: white;
}

.map-control-icon i {
  font-size: 16px;
}

.zoom-controls {
  display: flex;
  flex-direction: column;
  background-color: rgba(28, 47, 123, 0.85);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.zoom-control-btn {
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #e3eafc;
  transition: all 0.3s;
}

.zoom-control-btn:hover {
  background-color: #3a5eea;
  color: white;
}

.zoom-control-btn:first-child {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.zoom-control-btn:nth-child(2) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

/* 🎯 工具分割线 */
.tool-separator {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.15);
  margin: 4px 0;
}

/* 🎯 激活工具样式 */
.active-tool {
  background-color: #3a5eea !important;
  color: white !important;
}

/* 🎯 定位对话框样式 */
.location-input-container {
  padding: 10px 0;
}

.decimal-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.decimal-input-group .el-input {
  flex: 1;
}

.decimal-input-group .el-select {
  flex-shrink: 0;
}

.dms-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dms-input-group .el-input {
  flex: 1;
}

.dms-input-group .el-select {
  flex-shrink: 0;
}

/* 🎯 优化后的度分秒输入框样式 */
.dms-input-group-optimized {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.dms-field {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.dms-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  margin: 0;
}

.dms-input {
  width: 70px;
}

.dms-symbol {
  font-size: 14px;
  font-weight: bold;
  color: #409EFF;
  margin-left: -8px;
  align-self: flex-end;
  margin-bottom: 8px;
}

/* 🎯 当前地图中心点显示样式 */
.current-map-center {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
}

.current-map-center h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #409EFF;
  font-weight: 600;
}

.center-coords p {
  margin: 4px 0;
  font-size: 13px;
  color: #333;
}

.center-coords strong {
  color: #666;
  font-weight: 500;
}

/* 🎯 度分秒精简模式样式 */
.dms-compact-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.dms-compact-input {
  width: 50px;
  flex-shrink: 0;
}

.dms-compact-symbol {
  font-size: 14px;
  font-weight: bold;
  color: #409EFF;
  margin: 0 2px;
  flex-shrink: 0;
}

.dms-compact-row .el-switch {
  margin-left: 8px;
  flex-shrink: 0;
}

/* 🎯 十进制switch模式样式 */
.decimal-switch-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.decimal-switch-input {
  flex: 1;
  min-width: 120px;
}

.decimal-symbol {
  font-size: 14px;
  font-weight: bold;
  color: #409EFF;
  margin: 0 4px;
  flex-shrink: 0;
}

.decimal-switch-row .el-switch {
  flex-shrink: 0;
}

/* 移除旧的全屏样式，因为我们现在使用浏览器原生全屏API */
</style>

<style>
/* 覆盖Leaflet比例尺样式，使其与我们的主题一致 */
.leaflet-control-scale-line {
  border-color: #e0e0e0 !important;
  background-color: #fff !important;
  color: #222 !important;
  margin-left: 10px !important;
  margin-bottom: 5px !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
}

/* 隐藏右下角标记 */
.leaflet-bottom.leaflet-right {
  display: none;
}

/* ==================== 测距工具样式 ==================== */

/* 测距起点标记 */
.measure-start-point {
  background: transparent !important;
  border: none !important;
}

.measure-start-marker {
  width: 12px;
  height: 12px;
  background-color: #ff4444;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* 测距预览提示 */
.measure-preview-tooltip-container {
  background: transparent !important;
  border: none !important;
}

.measure-preview-tooltip {
  background-color: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  text-align: center;
}

.measure-preview-distance {
  font-weight: bold;
  color: #ffcc00;
}

.measure-preview-bearing {
  font-size: 11px;
  color: #aaa;
}

/* 测距线段标签 */
.measure-label {
  background-color: rgba(255, 68, 68, 0.9);
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transform-origin: center center;
}

.measure-distance {
  font-weight: bold;
  font-size: 13px;
}

.measure-bearing {
  font-size: 11px;
  opacity: 0.9;
}

/* 测距删除标记 */
.measure-delete-marker {
  width: 20px;
  height: 20px;
  background-color: rgba(255, 68, 68, 0.9);
  border: 2px solid #fff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
}

.measure-delete-marker:hover {
  background-color: #ff0000;
  transform: scale(1.1);
}

.measure-delete-marker i {
  color: #fff;
  font-size: 12px;
  font-weight: bold;
}

/* 隐藏测距标记的默认leaflet样式 */
[class^="measure-delete-container-"],
[class^="custom-measure-icon-"] {
  background: transparent !important;
  border: none !important;
}
</style> 